import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { Conversation } from '../types/chat';
import { getConversations } from '../components/chat/api/conversationAPI';
import NewConversationModal from '../components/chat/NewConversationModal';
import { FaPlus, FaComments } from 'react-icons/fa';

const ConversationListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguage();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, selectedLanguage]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await getConversations();
      // Filter conversations by selected language
      const filtered = selectedLanguage
        ? data.filter((conv: Conversation) => conv.conversation_language_iso === selectedLanguage.iso639_tag)
        : data;
      setConversations(filtered);
      setError(null);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const handleConversationSelect = (conversationId: number) => {
    navigate(`/conversations/${conversationId}`);
  };

  const handleNewConversation = () => {
    setShowNewConversationModal(true);
  };

  const handleConversationCreated = (conversationId: number) => {
    setRefreshTrigger(prev => prev + 1);
    navigate(`/conversations/${conversationId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return t('conversations.today');
    } else if (days === 1) {
      return t('conversations.yesterday');
    } else if (days < 7) {
      return t('conversations.daysAgo', { count: days });
    } else {
      return date.toLocaleDateString();
    }
  };

  const getProficiencyColor = (level: string) => {
    const colors: { [key: string]: string } = {
      A1: 'badge-success',
      A2: 'badge-info',
      B1: 'badge-warning',
      B2: 'badge-primary',
      C1: 'badge-secondary',
      C2: 'badge-error',
    };
    return colors[level] || 'badge-ghost';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full pt-20 pb-20">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 pt-20">
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Content area with padding for fixed header/footer */}
      <div className="flex-1 overflow-y-auto pt-16 pb-[7rem]">
        <div className="px-4 py-3 sticky top-0 bg-base-100/95 backdrop-blur-sm z-10 border-b border-base-300">
          <div className="flex items-center justify-between gap-3">
            {selectedLanguage && (
              <span className="text-xs text-base-content/60 truncate">
                {t('grammar.practicing', { language: selectedLanguage.language_name })}
              </span>
            )}
            <button
              onClick={handleNewConversation}
              className="btn btn-primary btn-sm gap-2"
            >
              <FaPlus /> {t('conversations.new')}
            </button>
          </div>
        </div>

        {/* Conversation List */}
        <div className="p-4 space-y-3">
          {conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FaComments size={64} className="text-base-content opacity-20 mb-4" />
              <p className="text-lg text-base-content opacity-60 mb-2">
                {t('conversations.noConversations')}
              </p>
              <p className="text-sm text-base-content opacity-40 mb-6">
                {selectedLanguage
                  ? t('conversations.startPractice', { language: selectedLanguage.language_name })
                  : t('conversations.startFirst')}
              </p>
              <button onClick={handleNewConversation} className="btn btn-primary gap-2">
                <FaPlus /> {t('conversations.startFirst')}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {conversations.map((conversation) => (
                <div
                  key={conversation.conversation_id}
                  onClick={() => handleConversationSelect(conversation.conversation_id)}
                  className="card bg-base-200 shadow-sm hover:shadow-md transition-all cursor-pointer active:scale-98"
                >
                  <div className="card-body p-4">
                    <div className="flex justify-between items-start">
                      <h3 className="card-title text-base font-semibold truncate flex-1">
                        {conversation.title}
                      </h3>
                      <span
                        className={`badge badge-sm ${getProficiencyColor(
                          conversation.proficiency_level
                        )} ml-2`}
                      >
                        {conversation.proficiency_level}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm opacity-70 mt-2">
                      <span className="truncate">
                        {conversation.conversation_language_name}
                      </span>
                      <span className="text-xs">
                        {formatDate(conversation.last_message_timestamp)}
                      </span>
                    </div>
                    {conversation.message_count !== undefined && (
                      <span className="text-xs opacity-60 mt-1">
                        {t('conversations.messageCount', { count: conversation.message_count })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      <NewConversationModal
        isOpen={showNewConversationModal}
        onClose={() => setShowNewConversationModal(false)}
        onConversationCreated={handleConversationCreated}
      />
    </div>
  );
};

export default ConversationListPage;
